import { Icon, TextField } from "@material-ui/core";
import moment from "moment";
import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import { presentations as presentationsApi } from "apis/callables";
import { THUMBNAIL_SIZES } from "common/constants";
import { IWorkspaceResourceSlide, IWorkspaceResourceSlideTemplate, WorkspaceResourceType, IPresentationMetadata } from "common/interfaces";
import { PresentationLibraryPresentation } from "js/controllers/PresentationLibraryController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import Thumbnails from "js/core/models/thumbnails";
import getUserProfile from "js/core/services/userProfiles";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { caseInsensitiveSort, trackActivity } from "js/core/utilities/utilities";
import { PresentationLibraryList } from "js/editor/PresentationLibrary/PresentationLibraryList";
import Avatar from "js/react/components/Avatar";
import { BeautifulDialog, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ConfirmationCheckboxDialog from "js/react/components/Dialogs/ConfirmationCheckboxDialog";
import { Gap10, Gap20, Gap30 } from "js/react/components/Gap";
import { FlexBox, ScrollBox } from "js/react/components/LayoutGrid";
import Loadable from "js/react/components/Loadable";
import { RedSwitch } from "js/react/components/Switch";
import TagsList from "js/react/components/TagsList";
import {
    BlueButton,
    Section,
    UIPane,
    UIPaneContents,
    UIPaneHeader,
} from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";
import { SlideThumbnail } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid.js";
import AddTagsDialog from "js/react/components/Dialogs/AddTagsDialog";

const logger = getLogger(LogGroup.TEAMS);

const SlideThumbnailContainer = styled.div`
    width: 640px;
    height: 360px;
    position: relative;
    
    .edit-message {
        opacity: 0;
        position: absolute;
        top: 0px;
        left: 0px;
        width: 100%;
        height: 100%;
        display: flex;
        align-items:  center;
        justify-content: center;
        padding: 50px;
        div {
          color: white;
          font-size: 18px;
          font-weight: 600;
          padding: 15px 20px;
          background: ${themeColors.ui_blue};
          text-align: center;
        }
    }
    &:hover {
      .edit-message {
        opacity: 1;
      }
    }
`;

const StatsContainer = styled.div`
    background: #f1f1f1;   
    width: 100%;
    padding: 15px;
    font-weight: 600;
    .MuiIcon-root {
        margin-right: 10px;
    }
    & > div + div {
        margin-top: 10px;
    }
`;

const UserInfo = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    label {
        font-size: 13px;
        width: 80px;
    }
    .avatar {
        margin-right: 10px;
    }
    .name {
        font-size: 14px;
        font-weight: 600;
        color: #333;
    }
    .date { 
        margin-left: 20px;
        font-size: 14px;
        color: #666;
    }
`;

interface TeamSlideDialogProps {
    slideId: string;
    closeDialog?: () => void;
}

const TeamSlideDialog = WorkspaceController.withInitializedState(
    function TeamSlideDialog(props: TeamSlideDialogProps & WorkspaceControllerState) {
        const { slideId, closeDialog, resources } = props;
        const { t } = useTranslation();

        const originalSlideResourceRef = useRef<IWorkspaceResourceSlide | IWorkspaceResourceSlideTemplate | undefined>();

        const [slideResource, setSlideResource] = useState<IWorkspaceResourceSlide | IWorkspaceResourceSlideTemplate | undefined>(
            () => {
                const initialResource = resources.find(resource => resource.resourceId === slideId) as IWorkspaceResourceSlide | IWorkspaceResourceSlideTemplate;
                originalSlideResourceRef.current = initialResource ? _.cloneDeep(initialResource) : undefined;
                return initialResource;
            }
        );

        const [isLoading, setIsLoading] = useState<boolean>(true);
        const [isLoadingUsedPresentations, setIsLoadingUsedPresentations] = useState<boolean>(true);
        const [usedPresentationsSort, setUsedPresentationsSort] = useState<{ field: string, reverse: boolean }>({ field: "name", reverse: false });
        const [usedPresentations, setUsedPresentations] = useState<PresentationLibraryPresentation[]>([]);
        const [createdUser, setCreatedUser] = useState<any>(null);
        const [modifiedUser, setModifiedUser] = useState<any>(null);

        useEffect(() => {
            (async () => {
                if (!slideResource) {
                    setIsLoading(false);
                    return;
                }
                setIsLoading(false);

                if (!slideResource.metadata.createdBy) {
                    setCreatedUser(null);
                    setModifiedUser(null);
                } else {
                    const [createdUserData, modifiedUserData] = await Promise.all([
                        getUserProfile(slideResource.metadata.createdBy),
                        slideResource.metadata.modifiedBy ? getUserProfile(slideResource.metadata.modifiedBy) : Promise.resolve(null)
                    ]);
                    setCreatedUser(createdUserData);
                    setModifiedUser(modifiedUserData);
                }

                await loadSlideUsedContainer(slideResource.metadata.usedInPresentations);
            })();
        }, [slideResource]);

        const loadSlideUsedContainer = async (presentationIds: string[]) => {
            //get all presentations
            let presentations: (IPresentationMetadata & { getThumbnailUrl?: (size?: "small" | "large") => Promise<string> })[] = [];
            if (presentationIds.length) {
                presentations = await presentationsApi.getPresentationsMetadata({ presentationIds, workspaceId: WorkspaceController.workspaceId });
            }

            presentations = presentations.filter(presentation => !presentation.trashedAt);
            for (let presentation of presentations) {
                // presentationlibrarylist expects a getThumbnailUrl function
                presentation.getThumbnailUrl = () => Thumbnails.getSignedUrlAndLoad(presentation.slideIds[0], presentation.firstSlideModifiedAt, presentation.id, THUMBNAIL_SIZES["small"].suffix, 0, false);
            }

            setIsLoadingUsedPresentations(false);
            setUsedPresentations(presentations as PresentationLibraryPresentation[]);
        };

        const handleSortUsedPresentations = (sort: { field: string, reverse: boolean }) => {
            let sortedData = _.sortBy(usedPresentations, o => {
                if (typeof o[sort.field] == "string") {
                    return o[sort.field].toLowerCase();
                } else {
                    return o[sort.field];
                }
            });

            if (sort.reverse) {
                sortedData = sortedData.reverse();
            }

            setUsedPresentationsSort(sort);
            setUsedPresentations(sortedData);
        };

        const handlePresentationDoubleClick = async (presentation: PresentationLibraryPresentation) => {
            const slideIndex = presentation.slideIds.indexOf(slideResource.resourceId) + 1;

            AppController.showEditor({
                presentationId: presentation.id,
                slideIndex,
                openInNewTab: true
            });

            trackActivity("SharedSlideProperties", "OpenPresentationFromUsedIn", null, null, {
                presentation_id: presentation.id,
                shared_slide_id: slideId
            });
        };

        const renderName = user => {
            if (user?.displayName) {
                return <span className="fullName">{user?.displayName}</span>;
            } else {
                return <span className="fullName">{user?.email}</span>;
            }
        };

        const handleEditSlide = () => {
            closeDialog();

            AppController.showSlideEditor({
                slideId: slideResource.resourceId,
                resourceType: slideResource.resourceType === WorkspaceResourceType.SLIDE_TEMPLATE ? "slide-templates" : "shared-slides"
            });
        };

        const handleDisableSlideToggle = async (isDisabled: boolean) => {
            if (!isDisabled) {
                ShowDialog(ConfirmationCheckboxDialog, {
                    title: t("Are you sure you want to hide this Shared Slide?"),
                    message: t("Hiding a Shared Slide prevents it from being used in future presentations."),
                    okButtonLabel: t("OK"),
                    acceptCallback: async () => {
                        setSlideResource(prev => {
                            if (!prev) return prev;
                            return {
                                ...prev,
                                metadata: {
                                    ...prev.metadata,
                                    isDisabled: !isDisabled
                                }
                            };
                        });
                        trackActivity("SharedSlideLibrary", "SlideDisabled", null, null, { slide_id: slideResource.resourceId });
                    }
                });
                return;
            }
            setSlideResource(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    metadata: {
                        ...prev.metadata,
                        isDisabled: !isDisabled
                    }
                };
            });
        };

        const handleTextChange = (text, type) => {
            setSlideResource(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    metadata: {
                        ...prev.metadata,
                        [type]: text
                    }
                };
            });
        };

        const handleRemoveTag = inputTag => {
            setSlideResource(prev => {
                if (!prev) return prev;
                // Remove the tag from the original array, using case-insensitive comparison
                // since the inputTag comes from the sorted/trimmed tagsList
                const newTags = prev.tags.filter(tag => _.trim(tag).toLowerCase() !== _.trim(inputTag).toLowerCase());
                return {
                    ...prev,
                    tags: newTags
                };
            });
        };

        const handleAddTag = () => {
            ShowDialog(AddTagsDialog, {
                activeTags: (slideResource.tags || []).map(_.trim),
                existingTags: WorkspaceController.existingTags,
                acceptCallback: (newTags: string[]) => {
                    setSlideResource(prev => {
                        if (!prev) return prev;
                        return {
                            ...prev,
                            tags: newTags
                        };
                    });
                },
            });
        };

        const handleCloseAndSave = async () => {
            if (!slideResource || !originalSlideResourceRef.current) {
                closeDialog && closeDialog();
                return;
            }

            const getNormalizedResource = resource => {
                return {
                    ...resource,
                    tags: caseInsensitiveSort(resource?.tags || [], _.trim),
                    metadata: {
                        ...resource.metadata,
                        name: resource.metadata.name || "",
                        description: resource.metadata.description || ""
                    }
                };
            };

            const normalizedCurrentResource = getNormalizedResource(slideResource);
            const normalizedOriginalResource = getNormalizedResource(originalSlideResourceRef.current);

            if (!_.isEqual(normalizedCurrentResource, normalizedOriginalResource)) {
                try {
                    await withProgressDialog({
                        title: t("Saving your changes..."),
                        action: () => WorkspaceController.updateWorkspaceResource(slideResource)
                    });
                    // Only close dialog after successful save
                    closeDialog && closeDialog();
                } catch (err) {
                    logger.error(err, "[TeamSlideDialog] workspace resource update failed");
                    ShowErrorDialog({
                        error: t("Failed to update team slide."),
                        message: err.message || t("An unknown error occurred.")
                    });
                    // Don't close dialog on error so user can see the error message
                }
            } else {
                // No changes detected, close dialog immediately
                closeDialog && closeDialog();
            }
        };

        const tagsList = caseInsensitiveSort(slideResource?.tags || [], _.trim);

        return (
            <BeautifulDialog maxWidth="lg" closeDialog={handleCloseAndSave} closeButton>
                <UIPane style={{ overflow: "hidden" }}>
                    <UIPaneHeader>
                        {slideResource?.resourceType === WorkspaceResourceType.SLIDE_TEMPLATE
                            ? t("Slide Template Properties")
                            : t("Shared Slide Properties")}
                    </UIPaneHeader>
                    <ScrollBox id="shared-slide-properties-content">
                        <UIPaneContents style={{ minHeight: 200 }}>
                            <Loadable isLoading={isLoading}>
                                <Section>
                                    <Gap10 />
                                    <FlexBox top left>
                                        <SlideThumbnailContainer>
                                            <SlideThumbnail slideId={slideResource?.resourceId} highQuality />
                                            <div className="edit-message">
                                                <BlueButton onClick={handleEditSlide}>{t("Edit Slide...")}</BlueButton>
                                            </div>
                                        </SlideThumbnailContainer>
                                        <Gap30 />
                                        <FlexBox column top left width={500}>
                                            <TextField
                                                id="library-name"
                                                autoFocus
                                                label={t("Name")}
                                                fullWidth
                                                value={slideResource?.metadata.name || ""}
                                                onChange={event => handleTextChange(event.target.value, "name")}
                                            />
                                            <Gap30 />

                                            <UserInfo>
                                                <label>{t("Created by")}</label>
                                                <Avatar url={createdUser?.photoURL} />
                                                <div className="name">{renderName(createdUser)}</div>
                                                <div className="date">{moment.utc(slideResource?.createdAt).local().format("LL")}</div>
                                            </UserInfo>
                                            <Gap20 />
                                            {modifiedUser &&
                                                <UserInfo>
                                                    <label>{t("Modified by")}</label>
                                                    <Avatar url={modifiedUser?.photoURL} />
                                                    <div className="name">{renderName(modifiedUser)}</div>
                                                    <div className="date">{moment.utc(slideResource?.metadata.contentModifiedAt).local().format("LL")}</div>
                                                </UserInfo>
                                            }

                                            <Gap20 />

                                            <StatsContainer>
                                                <FlexBox left middle>
                                                    <Icon>star</Icon>
                                                    {slideResource?.metadata.ratedByUsers.length} {slideResource?.metadata.ratedByUsers.length === 1 ? t("like") : t("likes")}
                                                </FlexBox>

                                                {slideResource?.resourceType !== WorkspaceResourceType.SLIDE_TEMPLATE &&
                                                    <FlexBox left middle>
                                                        <Icon>person</Icon>
                                                        {t("Used by {{count}} {{people}}", {
                                                            count: slideResource?.metadata.usedByUsers.length,
                                                            people: slideResource?.metadata.usedByUsers.length == 1 ? t("person") : t("people")
                                                        })}
                                                    </FlexBox>
                                                }

                                                {slideResource?.resourceType !== WorkspaceResourceType.SLIDE_TEMPLATE &&
                                                    <FlexBox left middle>
                                                        <Icon>cloud_download</Icon>
                                                        {t("Used in {{count}} {{presentations}}", {
                                                            count: slideResource?.metadata.usedInPresentations.length,
                                                            presentations: slideResource?.metadata.usedInPresentations.length === 1 ? t("presentation") : t("presentations")
                                                        })}
                                                    </FlexBox>
                                                }
                                            </StatsContainer>
                                            <Gap20 />
                                            <RedSwitch
                                                id="hideSlideSwitch"
                                                checked={slideResource?.metadata.isDisabled}
                                                onChange={() => handleDisableSlideToggle(slideResource?.metadata.isDisabled)}
                                                value="disableSlide"
                                                label={t("Hide This Slide")}
                                            />
                                        </FlexBox>
                                    </FlexBox>
                                    <Gap20 />
                                    <TextField multiline
                                        fullWidth
                                        value={slideResource?.metadata.description || ""}
                                        placeholder={t("Add a description or instructions for this slide")}
                                        variant="outlined"
                                        onChange={event => handleTextChange(event.target.value, "description")}
                                    />

                                </Section>
                                <Section>
                                    <FlexBox left middle fillWidth>
                                        <div className="section-title">{t("Tags")}</div>
                                        <Gap20 />
                                        <TagsList
                                            tags={tagsList}
                                            onRemove={handleRemoveTag}
                                            onClick={handleAddTag}
                                        />
                                    </FlexBox>
                                </Section>
                                {slideResource?.resourceType !== WorkspaceResourceType.SLIDE_TEMPLATE &&
                                    <Section title={t("Where is this slide used")}>
                                        <Loadable isLoading={isLoadingUsedPresentations}>
                                            {(usedPresentations.length > 0) &&
                                                <PresentationLibraryList
                                                    filteredPresentations={usedPresentations}
                                                    sort={usedPresentationsSort}
                                                    onSort={handleSortUsedPresentations}
                                                    onDoubleClick={handlePresentationDoubleClick}
                                                    readOnly={false}
                                                />
                                            }
                                            {(usedPresentations.length == 0) &&
                                                <label>{t("This team slide is not used in any presentations.")}</label>
                                            }
                                        </Loadable>
                                    </Section>
                                }
                            </Loadable>
                        </UIPaneContents>
                    </ScrollBox>
                </UIPane>
            </BeautifulDialog>
        );
    }
);

export default TeamSlideDialog;
